import {
  FormControl,
  FormHelperText,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useTheme,
} from '@mui/material';
import { getTemplatesViewSx } from '@src/components/_shared/sxStyles/Config/templates';
import FormattedAlias from '@src/components/_shared/Translations/FormattedAlias';
import useAlias from '@src/hooks/useAlias';
import storeConnector from '@src/store/storeConnector';
import { ISections } from '@src/types/Stacks';
import { Controller, useFormContext } from 'react-hook-form';
import { FormValues } from './TemplatesCard/types';

interface TemplatesFieldsProps {
  inputFields: ISections['fields'];
}

function TemplatesFields({ inputFields }: TemplatesFieldsProps) {
  const theme = useTheme();
  const templatesViewSx = getTemplatesViewSx(theme);
  const alias = useAlias();
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();

  return (
    <Grid spacing={2} container>
      {inputFields.map((field, index) => {
        const fieldName = field.controllerField;

        return (
          <Grid key={index} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <FormControl
              fullWidth
              key={index}
              sx={templatesViewSx.fields}
              error={!!errors[fieldName]}
            >
              {['string', 'number', 'boolean'].includes(field.type) && (
                <Controller
                  name={fieldName}
                  control={control}
                  rules={{
                    required: alias('{field} is required', {
                      field: field.uiLabel,
                    }),
                    ...(field.type === 'number' && {
                      min: {
                        value: field.range?.min ?? -Infinity,
                        message: alias('Minimum value is {min}', {
                          min: field.range?.min ?? -Infinity,
                        }),
                      },
                      max: {
                        value: field.range?.max ?? Infinity,
                        message: alias('Maximum value is {max}', {
                          max: field.range?.max ?? Infinity,
                        }),
                      },
                    }),
                  }}
                  render={({ field: { onChange, value, name, ref } }) => (
                    <>
                      <TextField
                        label={field.uiLabel}
                        variant='outlined'
                        size='small'
                        value={value ?? ''}
                        name={name}
                        inputRef={ref}
                        type={field.type === 'string' ? 'text' : 'number'}
                        onChange={(e) => {
                          const val =
                            field.type === 'number'
                              ? e.target.value === ''
                                ? ''
                                : Number(e.target.value)
                              : e.target.value;
                          onChange(val);
                        }}
                        inputProps={{
                          ...(field.type === 'number'
                            ? {
                                min: field.range?.min,
                                max: field.range?.max,
                                step: 'any',
                              }
                            : {}),
                          ...(field.type === 'boolean'
                            ? {
                                min: 0,
                                max: 1,
                                step: 1,
                              }
                            : {}),
                        }}
                        error={!!errors[fieldName]}
                      />
                      {errors[fieldName] && (
                        <FormHelperText>
                          {errors[fieldName]?.message?.toString()}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              )}
              {field.type === 'select' && (
                <Controller
                  name={fieldName}
                  control={control}
                  rules={{
                    required: alias('{field} is required', {
                      field: field.uiLabel,
                    }),
                  }}
                  render={({ field: { onChange, value, name, ref } }) => (
                    <>
                      <InputLabel id={`select-${fieldName}`}>
                        {field.uiLabel}
                      </InputLabel>
                      <Select
                        label={field.uiLabel}
                        labelId={`select-${fieldName}`}
                        variant='outlined'
                        size='small'
                        value={value ?? ''}
                        name={name}
                        inputRef={ref}
                        onChange={onChange}
                        error={!!errors[fieldName]}
                      >
                        {field.listOptions?.map(
                          (option: string, index: number) => (
                            <MenuItem key={index} value={option}>
                              <FormattedAlias id={option} />
                            </MenuItem>
                          ),
                        )}
                      </Select>
                      {errors[fieldName] && (
                        <FormHelperText>
                          {errors[fieldName]?.message?.toString()}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              )}
            </FormControl>
          </Grid>
        );
      })}
    </Grid>
  );
}

export default storeConnector(TemplatesFields, {
  config: ['siteMeta'],
});
